import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { localStorageService } from "@/services/LocalStorageService";

interface UseColumnWidthsArgs {
	enabled: boolean;
	localStorageKey: string | undefined;
}

interface UseColumnWidthsResult {
	/** `{ field: pixelWidth }` for columns the user has manually resized (empty when none / disabled). */
	columnWidths: Record<string, number>;
	/** Record a user resize: update state and persist (when keyed). */
	setColumnWidth: (field: string, width: number) => void;
	/** Clear all persisted widths (part of a layout reset). */
	resetWidths: () => void;
}

/**
 * Owns optional, persisted column WIDTHS for SystemDataGrid. Stores only the columns the user actually
 * resized as a `{ field: pixelWidth }` map, per-user under the table's `localStorageKey` + a `-columnWidths`
 * suffix (distinct from sort/order/visibility). Structural mirror of `useColumnVisibility` (including the
 * async-userId restore, since `useAuth` populates `userInfo` after mount).
 *
 * Applying these widths back onto the column definitions (done by the grid) makes a resize survive both a
 * `columns`-prop rebuild within the session — MUI otherwise discards resized widths whenever the columns
 * array identity changes — and re-entering the page later. When `enabled` is false the hook is inert.
 */
export function useColumnWidths({ enabled, localStorageKey }: UseColumnWidthsArgs): UseColumnWidthsResult {
	const { userInfo } = useAuth();
	const userId = userInfo?.id ?? "";

	const [columnWidths, setWidthsState] = useState<Record<string, number>>(() => {
		const stored = enabled && localStorageKey && userId ? localStorageService.getTableColumnWidths(localStorageKey, userId) : null;
		return stored ?? {};
	});

	// `useAuth` populates userInfo asynchronously; if userId was empty at mount, retry the restore once it
	// arrives (mirrors useColumnVisibility / useColumnOrder). No-op if nothing was saved.
	const initialUserIdHadValueRef = useRef(!!userId);
	useEffect(() => {
		if (initialUserIdHadValueRef.current || !userId || !enabled || !localStorageKey) return;
		initialUserIdHadValueRef.current = true;
		const stored = localStorageService.getTableColumnWidths(localStorageKey, userId);
		if (stored) setWidthsState(stored);
	}, [userId, enabled, localStorageKey]);

	const setColumnWidth = useCallback(
		(field: string, width: number) => {
			setWidthsState((prev) => {
				if (prev[field] === width) return prev;
				const next = { ...prev, [field]: width };
				if (enabled && localStorageKey && userId) localStorageService.saveTableColumnWidths(localStorageKey, next, userId);
				return next;
			});
		},
		[enabled, localStorageKey, userId],
	);

	const resetWidths = useCallback(() => {
		setWidthsState({});
		if (enabled && localStorageKey && userId) localStorageService.removeTableColumnWidths(localStorageKey, userId);
	}, [enabled, localStorageKey, userId]);

	return { columnWidths, setColumnWidth, resetWidths };
}
